<template>
  <div class="row-span-2 grid grid-cols-2 gap-x-4">
    <div v-for="field in templateFields" :key="field.name">
      <component
        :is="field.component"
        v-bind="omit(field, ['component', 'events'])"
        v-on="field.events"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { type PropType, computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import omit from "lodash/omit";

import { FULL_MONTHS } from "@/helpers/constants";
import { useIndustryOptions, useNumberOfEmployees } from "@/hooks/options";

import type { IBusinessEntityProfile } from "@/models/businesses";

const emit = defineEmits<{
  (e: "industryTypeChange"): void;
  (e: "websiteAddressChange", event: Event): void;
}>();

const props = defineProps({
  data: {
    type: Object as PropType<IBusinessEntityProfile | null>,
  },
});

const { industryTypesOptions, subIndustryTypesOptions } = useIndustryOptions();
const { numberOfEmployeesOptionsLabels, numberOfEmployeeIndexById } =
  useNumberOfEmployees();
const { t } = useI18n();

const selectedIndustryId = ref(0);

const templateFields = computed(() => [
  {
    component: "lf-dropdown",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.FISCAL_YEAR_END"),
    options: FULL_MONTHS,
    name: "fiscal_year_end",
  },
  {
    component: "lf-input",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.NAICS"),
    name: "naics_code",
  },
  {
    component: "lf-input",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.DUNS"),
    name: "duns_number",
  },
  {
    component: "lf-input",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.BIN"),
    name: "experian_bin",
  },
  {
    component: "lf-dropdown",
    name: "industry_type_id",
    sort: true,
    clearFilter: true,
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.INDUSTRY_TYPE"),
    options: industryTypesOptions.value,
    searchEnabled: true,
    fullWidth: true,
    events: { change: onIndustryTypeChange },
    class: "mb-5",
  },
  {
    component: "lf-dropdown",
    name: "sub_industry_type_id",
    sort: true,
    disabled: !selectedIndustryId.value,
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.SUB_INDUSTRY"),
    options: subIndustryTypesOptions(selectedIndustryId.value),
    searchEnabled: true,
    fullWidth: true,
    class: "mb-5",
  },
  {
    component: "lf-input",
    name: "industry_other",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.INDUSTRY_OTHER"),
  },
  {
    component: "lf-dropdown",
    name: "number_of_employees_id",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.NO_OF_EMPLOYEES"),
    options: numberOfEmployeesOptionsLabels.value,
    value: numberOfEmployeeIndexById(
      Number(props.data?.number_of_employees_id)
    ),
  },
  {
    component: "lf-input",
    name: "sic_code",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.SIC_CODE"),
  },
  {
    component: "lf-input",
    name: "business_management_software",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.HAS_ACCOUNTING_SOFTWARE"),
  },
  {
    component: "lf-input",
    placeholder: t("COMMON.COMPANY_WEBSITE"),
    name: "website_address",
    type: "url",
    events: { change: handleWebsiteAddressChange },
  },
  {
    component: "lf-input",
    name: "w2_number_of_employees_2020",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.W2_EMPLOYEES_2020"),
  },
  {
    component: "lf-input",
    name: "w2_number_of_employees_2021",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.W2_EMPLOYEES_2021"),
  },
  {
    component: "lf-currency",
    name: "current_business_debt",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.CURRENT_BUSINESS_DEBT"),
    noMargin: true,
    modelValue: props.data?.current_business_debt,
  },
  {
    component: "lf-currency",
    name: "stated_average_balance",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.AVERAGE_BALANCE"),
    noMargin: true,
    modelValue: props.data?.stated_average_balance,
  },
  {
    component: "lf-input",
    name: "custom_field",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.CUSTOM_FIELD"),
  },
  {
    component: "lf-input",
    name: "dot_number",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.DOT_NUMBER"),
  },
]);

const onIndustryTypeChange = (industryId: string) => {
  selectedIndustryId.value = Number(industryId);
  emit("industryTypeChange");
};

const handleWebsiteAddressChange = (e: Event) => {
  emit("websiteAddressChange", e);
};

onMounted(() => {
  selectedIndustryId.value = Number(props.data?.industry_type_id ?? 0);
});
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.row {
  @apply flex flex-row flex-wrap space-x-0 sm:space-x-5;
}
</style>
